import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiImage, FiExternalLink, FiZoomIn, FiX, FiAlertCircle, FiInfo } = FiIcons;

const ImagesSection = ({ images }) => {
  const [selectedImage, setSelectedImage] = useState(null);

  // Debug log to see what images are being passed
  React.useEffect(() => {
    console.log('ImagesSection received images:', images?.length || 0);
  }, [images]);

  if (!images || images.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.15 }}
        className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-purple-500/20"
      >
        <div className="flex items-center space-x-3 mb-6">
          <SafeIcon icon={FiImage} className="text-2xl text-purple-400" />
          <h3 className="text-2xl font-bold text-white">🖼️ TOP IMAGES</h3>
          <span className="text-purple-300 text-sm ml-auto">No images found</span>
        </div>
        
        <div className="text-center py-8">
          <SafeIcon icon={FiAlertCircle} className="text-4xl text-yellow-400 mx-auto mb-4" />
          <p className="text-purple-300 mb-4">
            No profile images were found for this person. This could mean:
          </p>
          <ul className="text-purple-400 text-sm mt-4 space-y-2 text-left max-w-md mx-auto">
            <li>• The person has a very limited online presence</li>
            <li>• Images are not publicly available</li>
            <li>• Search API limits were reached</li>
            <li>• Privacy settings restrict access</li>
            <li>• Google API credentials may be invalid</li>
          </ul>
          <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg max-w-md mx-auto">
            <div className="flex items-center space-x-2 mb-2">
              <SafeIcon icon={FiInfo} className="text-blue-400" />
              <span className="text-blue-300 text-sm font-medium">Pro Tip</span>
            </div>
            <p className="text-blue-400 text-xs">
              For better image results, ensure your Google API Key and Search Engine ID (CX) are properly configured in the Advanced Settings.
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.15 }}
      className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-purple-500/20"
    >
      <div className="flex items-center space-x-3 mb-6">
        <SafeIcon icon={FiImage} className="text-2xl text-purple-400" />
        <h3 className="text-2xl font-bold text-white">🖼️ TOP IMAGES</h3>
        <span className="text-purple-300 text-sm ml-auto">
          {images.length} image{images.length !== 1 ? 's' : ''} found
        </span>
      </div>

      {/* Demo images warning */}
      {images.some(img => img.source === 'Demo (Placeholder)') && (
        <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiInfo} className="text-yellow-400" />
            <p className="text-yellow-300 text-sm">
              Some images are demo placeholders. Real image search requires valid Google API credentials.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {images.map((image, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="relative group cursor-pointer"
            onClick={() => setSelectedImage(image)}
          >
            <div className="aspect-square rounded-lg overflow-hidden bg-black/30 border border-purple-500/20">
              <img 
                src={image.thumbnail} 
                alt={image.title} 
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                onError={(e) => {
                  // Fallback to placeholder if image fails to load
                  e.target.src = `https://via.placeholder.com/200x200?text=${encodeURIComponent(image.title.substring(0, 10))}`;
                }}
                loading="lazy"
              />
            </div>
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg">
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <p className="text-white text-xs font-medium line-clamp-2">
                  {image.title}
                </p>
                <p className="text-purple-300 text-xs mt-1">
                  Source: {image.source}
                </p>
              </div>
              <div className="absolute top-2 right-2">
                <SafeIcon icon={FiZoomIn} className="text-white bg-black/50 p-1 rounded" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Image Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative max-w-4xl max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute -top-12 right-0 text-white hover:text-purple-300 transition-colors"
              >
                <SafeIcon icon={FiX} className="text-2xl" />
              </button>
              <div className="bg-black/50 backdrop-blur-lg rounded-lg p-4 border border-purple-500/20">
                <img 
                  src={selectedImage.link} 
                  alt={selectedImage.title} 
                  className="max-w-full max-h-[70vh] object-contain rounded-lg"
                  onError={(e) => {
                    e.target.src = `https://via.placeholder.com/400x400?text=${encodeURIComponent(selectedImage.title)}`;
                  }}
                />
                <div className="mt-4 flex items-center justify-between">
                  <div>
                    <h4 className="text-white font-medium">{selectedImage.title}</h4>
                    <p className="text-purple-300 text-sm mt-1">
                      {selectedImage.width && selectedImage.height && `Size: ${selectedImage.width}×${selectedImage.height}px`}
                      {selectedImage.size && ` | ${(selectedImage.size / 1024).toFixed(1)}KB`}
                    </p>
                    <p className="text-purple-400 text-xs mt-1">Source: {selectedImage.source}</p>
                    {selectedImage.strategy && (
                      <p className="text-purple-400 text-xs">Found with: {selectedImage.strategy}</p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    {selectedImage.contextLink && (
                      <a 
                        href={selectedImage.contextLink} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="flex items-center space-x-1 text-blue-400 hover:text-blue-300 text-sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <SafeIcon icon={FiExternalLink} />
                        <span>View Source</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ImagesSection;